import { useEffect, useState } from 'react';
import { AppearanceDefaultsTab } from './AppearanceDefaultsTab';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarPlus, CheckCircle2, Ban, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { API_BASE } from '@/utils/api';
import { LegalTab, TrialExceptionsTab } from './SecurityTabs';

const STATUS = { trial: ['Prueba', 'bg-sky-100 text-sky-800'], active: ['Activa', 'bg-green-100 text-green-800'], past_due: ['Vencida (gracia)', 'bg-amber-100 text-amber-800'], suspended: ['Suspendida', 'bg-red-100 text-red-800'], cancelled: ['Cancelada', 'bg-slate-200 text-slate-700'] };
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('es-AR') : '—');
const err = (e) => toast.error(e.response?.data?.detail || 'Error');

const SubscriptionsTab = () => {
  const [rows, setRows] = useState([]);
  const [upcoming, setUpcoming] = useState(false);
  const load = () => axios.get(`${API_BASE}/admin/subscriptions`, { params: upcoming ? { upcoming_days: 7 } : {} }).then(r => setRows(r.data)).catch(err);
  useEffect(() => { load(); }, [upcoming]); // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (path, body, ok) => { try { await axios.post(`${API_BASE}/admin/subscriptions/${path}`, body); toast.success(ok); load(); } catch (e) { err(e); } };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant={upcoming ? 'default' : 'outline'} onClick={() => setUpcoming(!upcoming)} data-testid="subs-upcoming-toggle">Vencen en 7 días</Button>
        <Button size="sm" variant="outline" onClick={() => act('run-cycle', {}, 'Ciclo ejecutado')} data-testid="subs-run-cycle-btn"><RefreshCw className="w-4 h-4 mr-1" />Ejecutar ciclo ahora</Button>
      </div>
      <div className="overflow-auto border border-border rounded-2xl">
        <table className="w-full text-sm" data-testid="subs-table">
          <thead className="bg-muted text-left"><tr><th className="p-2">Negocio</th><th className="p-2">Estado</th><th className="p-2">Vence</th><th className="p-2">Días</th><th className="p-2">Acciones</th></tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.business_id} className="border-t border-border" data-testid={`subs-row-${r.business_id}`}>
                <td className="p-2">{r.name}</td>
                <td className="p-2"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS[r.status]?.[1]}`} data-testid={`subs-status-${r.business_id}`}>{STATUS[r.status]?.[0]}</span></td>
                <td className="p-2">{fmt(r.ends_at)}</td>
                <td className="p-2">{r.days_left ?? '—'}</td>
                <td className="p-2 flex flex-wrap gap-1">
                  <Button size="sm" variant="outline" onClick={() => act(`${r.business_id}/extend-trial`, { days: 7 }, 'Prueba extendida 7 días')} data-testid={`subs-extend-${r.business_id}`}><CalendarPlus className="w-3.5 h-3.5 mr-1" />+7 días prueba</Button>
                  <Button size="sm" variant="outline" onClick={() => act(`${r.business_id}/activate`, { days: 30, reference: 'manual' }, 'Activada 30 días')} data-testid={`subs-activate-${r.business_id}`}><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Activar</Button>
                  {r.status !== 'cancelled' && <Button size="sm" variant="ghost" className="text-red-600" onClick={() => window.confirm('¿Cancelar la suscripción? Los datos se conservan.') && act(`${r.business_id}/cancel`, {}, 'Cancelada')} data-testid={`subs-cancel-${r.business_id}`}><Ban className="w-3.5 h-3.5" /></Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const OnboardingTab = () => {
  const [d, setD] = useState(null);
  useEffect(() => { axios.get(`${API_BASE}/admin/onboarding/stats`).then(r => setD(r.data)).catch(err); }, []);
  if (!d) return null;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="onboarding-funnel">
        {d.steps.map(s => (
          <div key={s} className="border border-border rounded-2xl p-3 text-sm" data-testid={`funnel-${s}`}>
            <div className="font-semibold">{d.labels[s]}</div>
            <div className="text-muted-foreground text-xs">Llegaron {d.funnel[s].reached} · completaron {d.funnel[s].completed}</div>
            <div className="text-xs">Promedio {Math.round(d.funnel[s].avg_sec / 60 * 10) / 10} min · abandonaron {d.funnel[s].abandoned}</div>
          </div>
        ))}
      </div>
      <div className="overflow-auto border border-border rounded-2xl">
        <table className="w-full text-xs" data-testid="onboarding-stats-table">
          <thead className="bg-muted text-left"><tr><th className="p-2">Negocio</th>{d.steps.map(s => <th key={s} className="p-2">{d.labels[s]}</th>)}<th className="p-2">Abandonó en</th></tr></thead>
          <tbody>{d.businesses.map(b => (
            <tr key={b.business_id} className="border-t border-border">
              <td className="p-2">{b.name}</td>
              {d.steps.map(s => <td key={s} className="p-2">{b.steps[s].status === 'done' ? '✓' : b.steps[s].status === 'skipped' ? 'salteado' : '—'} {b.steps[s].time_spent_sec ? `${Math.round(b.steps[s].time_spent_sec)}s` : ''}</td>)}
              <td className="p-2 text-red-600">{b.abandoned_at ? d.labels[b.abandoned_at] : ''}</td>
            </tr>))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AiUsageTab = () => {
  const [d, setD] = useState(null);
  useEffect(() => { axios.get(`${API_BASE}/admin/menu-ai/usage`).then(r => setD(r.data)).catch(err); }, []);
  if (!d) return null;
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="ai-usage-panel">
      <div className="border border-border rounded-2xl p-3 text-sm space-y-1">
        <div className="font-semibold mb-1">Por negocio</div>
        {d.by_business.map(b => <div key={b.business_id}>{b.name}: {b.success} ok · {b.errors} error · {b.rejected} sin cupo</div>)}
      </div>
      <div className="border border-border rounded-2xl p-3 text-xs space-y-1 max-h-72 overflow-auto">
        <div className="font-semibold text-sm mb-1">Últimas lecturas</div>
        {d.recent.map(r => <div key={r.read_id}>{new Date(r.at).toLocaleString('es-AR')} · {r.business_name} · {r.status}{r.products ? ` · ${r.products} productos` : ''}</div>)}
      </div>
    </div>
  );
};

const PlanTab = () => {
  const [plan, setPlan] = useState(null);
  useEffect(() => { axios.get(`${API_BASE}/admin/plans`).then(r => setPlan(r.data.find(p => p.is_default) || r.data[0])).catch(err); }, []);
  if (!plan) return null;
  const fields = [['trial_days', 'Días de prueba'], ['grace_days', 'Días de gracia'], ['period_days', 'Días por período'], ['price', 'Precio'], ['menu_ai_trial_limit', 'Lecturas IA en prueba'], ['menu_ai_monthly_limit', 'Lecturas IA por mes']];
  const save = async () => {
    try {
      const body = Object.fromEntries(fields.map(([k]) => [k, Number(plan[k])]));
      await axios.put(`${API_BASE}/admin/plans/${plan.plan_id}`, body);
      toast.success('Plan guardado');
    } catch (e) { err(e); }
  };
  return (
    <div className="space-y-3 max-w-xl" data-testid="plan-editor">
      <p className="text-sm font-semibold">{plan.name}</p>
      <div className="grid grid-cols-2 gap-3">
        {fields.map(([k, l]) => <label key={k} className="text-xs space-y-1"><span>{l}</span><Input type="number" min={0} value={plan[k]} onChange={e => setPlan({ ...plan, [k]: e.target.value })} data-testid={`plan-${k}-input`} /></label>)}
      </div>
      <Button size="sm" onClick={save} data-testid="plan-save-btn">Guardar plan</Button>
    </div>
  );
};

export const PlatformInsights = () => (
  <div className="bg-card border border-border rounded-3xl p-4 mb-8" data-testid="platform-insights">
    <Tabs defaultValue="subs">
      <TabsList className="mb-4 flex-wrap h-auto">
        <TabsTrigger value="subs" data-testid="tab-subscriptions">Suscripciones</TabsTrigger>
        <TabsTrigger value="onb" data-testid="tab-onboarding">Alta de negocios</TabsTrigger>
        <TabsTrigger value="ai" data-testid="tab-ai-usage">Lecturas IA</TabsTrigger>
        <TabsTrigger value="plan" data-testid="tab-plan">Plan</TabsTrigger>
        <TabsTrigger value="legal" data-testid="tab-legal">Legales</TabsTrigger>
        <TabsTrigger value="cuit" data-testid="tab-trial-exceptions">Excepciones CUIT</TabsTrigger>
        <TabsTrigger value="appearance" data-testid="tab-appearance-defaults">Tema</TabsTrigger>
      </TabsList>
      <TabsContent value="subs"><SubscriptionsTab /></TabsContent>
      <TabsContent value="onb"><OnboardingTab /></TabsContent>
      <TabsContent value="ai"><AiUsageTab /></TabsContent>
      <TabsContent value="plan"><PlanTab /></TabsContent>
      <TabsContent value="legal"><LegalTab /></TabsContent>
      <TabsContent value="cuit"><TrialExceptionsTab /></TabsContent>
      <TabsContent value="appearance"><AppearanceDefaultsTab /></TabsContent>
    </Tabs>
  </div>
);
